/**
 * The controls behind the automatic event countdowns (lib/liveEventPrefs,
 * 2026-10-30): card or notification, how long before, and the event-type
 * checklist. One component, used by the intro sheet and by Settings, so the
 * two never drift.
 *
 * Visual-first: each type is its own coloured pill (the Calendar's own
 * EventTypePill colours and icons), ticked or dimmed, never a list of
 * sentences.
 */
import { Check } from "lucide-react";
import { eventTypeMeta } from "@/components/EventTypePill";
import {
  LEAD_OPTIONS,
  LIVE_EVENT_TYPES,
  type LiveEventDelivery,
  type LiveEventLeadMinutes,
  type LiveEventPrefs,
  type LiveEventType,
} from "@/lib/liveEventPrefs";

function Segmented<T extends string | number>({
  label,
  value,
  options,
  onChange,
  testId,
}: {
  label: string;
  value: T;
  options: ReadonlyArray<{ key: T; label: string; hint?: string }>;
  onChange: (next: T) => void;
  testId: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="grid gap-1 rounded-lg p-1"
      style={{
        gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
        background: "hsl(var(--card))",
        border: "1px solid hsl(var(--border))",
      }}
    >
      {options.map((o) => {
        const active = o.key === value;
        return (
          <button
            key={String(o.key)}
            type="button"
            onClick={() => onChange(o.key)}
            aria-pressed={active}
            title={o.hint}
            data-testid={`${testId}-${String(o.key)}`}
            className={`rounded-md py-1.5 px-1 text-[11px] font-semibold leading-tight transition ${active ? "" : "hover-elevate"}`}
            style={
              active
                ? { background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }
                : { background: "transparent", color: "hsl(var(--muted-foreground))" }
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function LiveEventPrefsControls({
  value,
  onChange,
  showDelivery,
  testId = "live-events",
}: {
  value: LiveEventPrefs;
  onChange: (next: LiveEventPrefs) => void;
  /** The card is a phone thing: on the web only the notification exists. */
  showDelivery: boolean;
  testId?: string;
}) {
  const toggleType = (t: LiveEventType) => {
    const types = value.types.includes(t) ? value.types.filter((x) => x !== t) : [...value.types, t];
    onChange({ ...value, types });
  };
  const setAll = (on: boolean) => onChange({ ...value, types: on ? [...LIVE_EVENT_TYPES] : [] });

  return (
    <div className="space-y-4">
      {showDelivery && (
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.14em] mb-1.5" style={{ color: "var(--text-ink-faint)" }}>
            How it shows
          </p>
          <Segmented<LiveEventDelivery>
            label="How the countdown shows"
            value={value.delivery}
            options={[
              { key: "live", label: "Countdown card", hint: "A live countdown on your Lock Screen or in your notifications" },
              { key: "push", label: "Notification only", hint: "One notification, no live countdown" },
            ]}
            onChange={(delivery) => onChange({ ...value, delivery })}
            testId={`${testId}-delivery`}
          />
        </div>
      )}

      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.14em] mb-1.5" style={{ color: "var(--text-ink-faint)" }}>
          How long before
        </p>
        <Segmented<LiveEventLeadMinutes>
          label="How long before the start"
          value={value.leadMinutes}
          options={LEAD_OPTIONS.map((o) => ({ key: o.minutes, label: o.short, hint: o.label }))}
          onChange={(leadMinutes) => onChange({ ...value, leadMinutes })}
          testId={`${testId}-lead`}
        />
      </div>

      <div>
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <p className="text-[10px] font-black uppercase tracking-[0.14em]" style={{ color: "var(--text-ink-faint)" }}>
            Which events
          </p>
          <div className="flex items-center gap-2 text-[10px] font-bold" style={{ color: "var(--accent-fill)" }}>
            <button type="button" onClick={() => setAll(true)} data-testid={`${testId}-types-all`}>
              All
            </button>
            <span aria-hidden style={{ color: "var(--text-ink-faint)" }}>
              ·
            </span>
            <button type="button" onClick={() => setAll(false)} data-testid={`${testId}-types-none`}>
              None
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Event types">
          {LIVE_EVENT_TYPES.map((t) => {
            const meta = eventTypeMeta(t);
            const Icon = meta.icon;
            const accent = `var(${meta.token})`;
            const on = value.types.includes(t);
            return (
              <button
                key={t}
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() => toggleType(t)}
                data-testid={`${testId}-type-${t}`}
                className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] whitespace-nowrap select-none transition"
                style={
                  on
                    ? {
                        color: accent,
                        background: `color-mix(in srgb, ${accent} 16%, transparent)`,
                        border: `1px solid color-mix(in srgb, ${accent} 55%, transparent)`,
                      }
                    : {
                        color: "var(--text-ink-faint)",
                        background: "transparent",
                        border: "1px dashed var(--border-subtle)",
                        opacity: 0.75,
                      }
                }
              >
                {on ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                {meta.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
